import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Tabs, Tab, Box, Link } from '@mui/material';
import ContestList from '../components/ContestList';
import Participants from '../components/Participants';
import AboutPage from './AboutPage';
import LatestEntries from '../components/LatestEntriesView';
import PodiumPage from "./PodiumPage";
import { useLocation, useNavigate } from 'react-router-dom';
import ProgressPage from "./ProgressPage";
import { useTheme } from '@mui/material/styles';

const HOME_ACTIVE_TAB = "homeActiveTab";

const Home: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();

    const getTabFromUrl = useCallback(() => {
        const params = new URLSearchParams(location.search);
        return parseInt(params.get("tab") || localStorage.getItem(HOME_ACTIVE_TAB) || '0', 10);
    }, [location.search]);

    const [activeTab, setActiveTab] = useState<number>(getTabFromUrl);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
        localStorage.setItem(HOME_ACTIVE_TAB, newValue.toString());
        navigate(`?tab=${newValue}`);
    };

    useEffect(() => {
        setActiveTab(getTabFromUrl());
    }, [getTabFromUrl]);

    return (
        <Box
            sx={{
                backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : theme.palette.background.default,
                color: theme.palette.text.primary,
                padding: { xs: '10px', md: '20px' },
            }}
        >
            {/* Title Section */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                        fontWeight: 'bold',
                        color: theme.palette.primary.main,
                        borderBottom: '2px solid',
                        borderColor: theme.palette.primary.main,
                        display: 'inline-block',
                        paddingBottom: '5px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                    }}
                >
                    Гиревые и другие зарубы телеграм-канала{' '}
                    <Link
                        href="https://t.me/SeregaPhotoskiff"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            color: theme.palette.secondary.main,
                            textDecoration: 'none',
                            borderBottom: '1px dotted',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        photoskiff
                    </Link>
                </Typography>
                <Typography variant="subtitle2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
                    Для публикации результатов сообщите в{' '}
                    <Link
                        href="https://t.me/SeregaPhotoskiffChat"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: theme.palette.primary.main, textDecoration: 'none' }}
                    >
                        чате канала
                    </Link>
                </Typography>
            </Box>

            {/* Tabs Section */}
            <Box mb={2}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    textColor="primary"
                    indicatorColor="primary"
                    sx={{
                        borderBottom: 1,
                        borderColor: theme.palette.divider,
                        justifyContent: 'flex-start',
                        '& .MuiTabs-scrollButtons.Mui-disabled': { display: 'none' },
                    }}
                >
                    <Tab label="Зарубы" sx={{ minWidth: 60, padding: '0 7px' }} />
                    <Tab label="Лента" sx={{ minWidth: 40, padding: '0 7px' }} />
                    <Tab label="Прогресс" sx={{ minWidth: 40, padding: '0 7px' }} />
                    <Tab label="Подиум" sx={{ minWidth: 40, padding: '0 7px' }} />
                    <Tab label="Участники" sx={{ minWidth: 60, padding: '0 7px' }} />
                    <Tab label="О Зарубах" sx={{ minWidth: 60, padding: '0 7px' }} />
                </Tabs>
            </Box>

            {/* Content Section */}
            <Box
                mt={3}
                sx={{
                    maxWidth: { xs: '100%', lg: '1200px' },
                    margin: '0 auto',
                    overflowX: 'auto',
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: 2,
                    boxShadow: theme.shadows[2],
                    padding: { xs: '10px', md: '20px' },
                }}
            >
                {activeTab === 0 && <ContestList />}
                {activeTab === 1 && <LatestEntries />}
                {activeTab === 2 && <ProgressPage />}
                {activeTab === 3 && <PodiumPage />}
                {activeTab === 4 && <Participants />}
                {activeTab === 5 && <AboutPage />}
            </Box>
        </Box>
    );
};

export default Home;
